import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Users, Award, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CoursesPage = () => {
  const courses = [
    {
      id: 'linux-fundamentals',
      title: 'Linux Fundamentals',
      description: 'Master command line and system administration. Build a strong foundation in Linux, the backbone of modern infrastructure.',
      duration: '4 weeks',
      level: 'Beginner',
      topics: ['Command Line Basics', 'File System Navigation', 'User Management', 'Process Management', 'Shell Scripting Intro']
    },
    {
      id: 'bash-scripting',
      title: 'Bash Scripting',
      description: 'Automate tasks and workflows with powerful shell scripts. Learn to write efficient, maintainable automation scripts.',
      duration: '3 weeks',
      level: 'Intermediate',
      topics: ['Variables & Data Types', 'Control Structures', 'Functions', 'Error Handling', 'Cron Jobs']
    },
    {
      id: 'git-github',
      title: 'Git & GitHub',
      description: 'Version control and collaboration essentials. Master the tools used by millions of developers worldwide.',
      duration: '2 weeks',
      level: 'Beginner',
      topics: ['Git Basics', 'Branching Strategies', 'Pull Requests', 'Code Reviews', 'GitHub Actions Intro']
    },
    {
      id: 'cloud-basics',
      title: 'Cloud Basics',
      description: 'AWS, Azure, and GCP essentials. Understand cloud computing fundamentals and multi-cloud strategies.',
      duration: '5 weeks',
      level: 'Beginner',
      topics: ['Cloud Concepts', 'AWS Core Services', 'Azure Fundamentals', 'GCP Overview', 'Cost Management']
    },
    {
      id: 'infrastructure-as-code',
      title: 'Infrastructure as Code',
      description: 'Terraform and Pulumi mastery. Provision and manage infrastructure using code-based approaches.',
      duration: '4 weeks',
      level: 'Intermediate',
      topics: ['Terraform Basics', 'State Management', 'Modules', 'Pulumi Introduction', 'Best Practices']
    },
    {
      id: 'containerization',
      title: 'Containerization',
      description: 'Docker and container orchestration. Package and deploy applications consistently across environments.',
      duration: '4 weeks',
      level: 'Intermediate',
      topics: ['Docker Fundamentals', 'Dockerfile Best Practices', 'Docker Compose', 'Image Optimization', 'Container Security']
    },
    {
      id: 'kubernetes',
      title: 'Kubernetes',
      description: 'Production-grade container management. Deploy, scale, and manage containerized applications at enterprise scale.',
      duration: '6 weeks',
      level: 'Advanced',
      topics: ['K8s Architecture', 'Deployments & Services', 'ConfigMaps & Secrets', 'Helm Charts', 'Monitoring & Logging']
    },
    {
      id: 'cicd-pipelines',
      title: 'CI/CD Pipelines',
      description: 'Automated deployment workflows. Build robust continuous integration and delivery pipelines.',
      duration: '4 weeks',
      level: 'Intermediate',
      topics: ['CI/CD Concepts', 'Jenkins', 'GitHub Actions', 'GitLab CI', 'Deployment Strategies']
    },
    {
      id: 'observability',
      title: 'Observability',
      description: 'Monitoring and logging systems. Gain visibility into your applications and infrastructure.',
      duration: '4 weeks',
      level: 'Advanced',
      topics: ['Prometheus', 'Grafana', 'ELK Stack', 'Distributed Tracing', 'Alerting Strategies']
    }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner':
        return 'bg-green-100 text-green-700';
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-700';
      case 'Advanced':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0F172A] to-[#1E293B]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            DevOps Training Courses
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto leading-relaxed">
            Comprehensive, hands-on training programs designed to transform you into a skilled DevOps professional. 
            From fundamentals to advanced practices.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-[#0EA5E9] mb-1">9</div>
              <div className="text-[#64748B] text-sm">Core Modules</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#0EA5E9] mb-1">36+</div>
              <div className="text-[#64748B] text-sm">Weeks of Content</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#0EA5E9] mb-1">50+</div>
              <div className="text-[#64748B] text-sm">Hands-on Labs</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#0EA5E9] mb-1">24/7</div>
              <div className="text-[#64748B] text-sm">Support Access</div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, idx) => (
              <div 
                key={course.id}
                className="bg-white border border-[#E2E8F0] rounded-sm hover:border-[#0EA5E9] hover:shadow-lg transition-all group"
                data-testid={`course-card-${course.id}`}
              >
                <div className="p-6">
                  {/* Module Number */}
                  <div className="text-[#0EA5E9] text-sm font-semibold mb-3">
                    Module {idx + 1}
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-bold text-[#1E293B] mb-3 group-hover:text-[#0EA5E9] transition-colors">
                    {course.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-[#64748B] text-sm leading-relaxed mb-4">
                    {course.description}
                  </p>
                  
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1.5 text-[#64748B] text-sm">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getLevelColor(course.level)}`}>
                      {course.level}
                    </span>
                  </div>
                  
                  {/* Topics */}
                  <div className="space-y-2 mb-6">
                    {course.topics.slice(0, 3).map((topic, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-[#475569]">
                        <CheckCircle className="w-4 h-4 text-[#0EA5E9]" />
                        {topic}
                      </div>
                    ))}
                    {course.topics.length > 3 && (
                      <div className="text-sm text-[#94A3B8]">
                        +{course.topics.length - 3} more topics
                      </div>
                    )}
                  </div>
                  
                  {/* CTA */}
                  <Link
                    to={`/dashboard`}
                    className="inline-flex items-center gap-2 text-[#0EA5E9] font-medium text-sm hover:gap-3 transition-all"
                    data-testid={`course-enroll-${course.id}`}
                  >
                    Start Learning
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Path Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-[#1E293B] mb-4">
            Structured Learning Path
          </h2>
          <p className="text-[#64748B] text-lg mb-12">
            Our curriculum is designed to take you from beginner to expert, with each module building on the previous one.
          </p>
          
          <div className="relative">
            {/* Timeline */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#E2E8F0] transform -translate-x-1/2"></div>
            
            <div className="space-y-8">
              {[
                { phase: 'Foundation', modules: 'Linux, Bash, Git', weeks: '9 weeks' },
                { phase: 'Cloud & IaC', modules: 'Cloud Basics, Terraform', weeks: '9 weeks' },
                { phase: 'Containers', modules: 'Docker, Kubernetes', weeks: '10 weeks' },
                { phase: 'Delivery', modules: 'CI/CD, Observability', weeks: '8 weeks' }
              ].map((item, idx) => (
                <div key={idx} className="relative flex items-center justify-center">
                  <div className="hidden md:block absolute left-1/2 w-4 h-4 bg-[#0EA5E9] rounded-full transform -translate-x-1/2 z-10"></div>
                  <div className="bg-white p-6 rounded-sm border border-[#E2E8F0] shadow-sm w-full md:w-80">
                    <div className="text-[#0EA5E9] font-semibold text-sm mb-1">Phase {idx + 1}</div>
                    <div className="text-[#1E293B] font-bold text-lg mb-1">{item.phase}</div>
                    <div className="text-[#64748B] text-sm">{item.modules}</div>
                    <div className="text-[#94A3B8] text-xs mt-2">{item.weeks}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F172A] to-[#1E293B]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your DevOps Journey?
          </h2>
          <p className="text-[#94A3B8] text-lg mb-10">
            Join thousands of professionals who have transformed their careers with Peabsmart DevOps Academy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0EA5E9] text-white font-semibold rounded-sm hover:bg-[#0284C7] transition-colors"
              data-testid="enroll-now-button"
            >
              Enroll Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-semibold rounded-sm border-2 border-white hover:bg-white hover:text-[#1E293B] transition-colors"
              data-testid="contact-us-button"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoursesPage;
